import { getAmenityTypeKeys } from '../../../components/amenities/AmenityTypes';
import { buildAmenityDeleteFields } from './amenitiesForm/amenityOverrideUtils';

const DETAIL_SCALAR_FIELDS = [
  'name',
  'address1',
  'city',
  'state',
  'zip',
  'phone',
  'email',
  'website',
  'isTempClosed',
];

function isEmptyValue(value) {
  if (value === '' || value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length === 0,
  );
}

function isExplicitlyCleared(rawValues, field) {
  return Object.prototype.hasOwnProperty.call(rawValues, field)
    && isEmptyValue(rawValues[field]);
}

function hadOverrideField(existingOverride, field) {
  return Boolean(
    existingOverride
    && Object.prototype.hasOwnProperty.call(existingOverride, field),
  );
}

function mergeAmenityPayload(payload, nestedDeletes, cleanedAmenities) {
  if (nestedDeletes) {
    payload.amenities = {
      ...nestedDeletes,
      ...(cleanedAmenities ?? {}),
    };
    return;
  }

  if (cleanedAmenities) {
    payload.amenities = cleanedAmenities;
  }
}

/**
 * Maps cleared override fields to Firestore deleteField() sentinels so merge
 * writes remove stale keys instead of leaving prior overrides untouched.
 */
export function applyClearedOverrideDeletes(
  rawValues,
  cleanedOverride,
  deleteFieldFn,
  existingOverride = null,
) {
  const payload = { ...cleanedOverride };

  for (const field of DETAIL_SCALAR_FIELDS) {
    if (
      isExplicitlyCleared(rawValues, field)
      && !(field in cleanedOverride)
      && hadOverrideField(existingOverride, field)
    ) {
      payload[field] = deleteFieldFn();
    }
  }

  if (rawValues.updateSocials === true && isEmptyValue(rawValues.socials)) {
    if (!('socials' in cleanedOverride)) {
      payload.socials = deleteFieldFn();
    }
    payload.updateSocials = deleteFieldFn();
  }

  if (rawValues.updateAmenities === true) {
    const amenityKeys = getAmenityTypeKeys();
    const nestedDeletes = buildAmenityDeleteFields(
      rawValues.amenities,
      existingOverride?.amenities,
      amenityKeys,
      deleteFieldFn,
    );

    if (isEmptyValue(rawValues.amenities)) {
      if (!('amenities' in cleanedOverride)) {
        payload.amenities = deleteFieldFn();
      }
      payload.updateAmenities = deleteFieldFn();
    } else {
      mergeAmenityPayload(payload, nestedDeletes, cleanedOverride.amenities);
    }
  }

  if (rawValues.updateOob === true) {
    if (
      !('isOob' in cleanedOverride)
      && rawValues.isOob === false
      && hadOverrideField(existingOverride, 'isOob')
    ) {
      payload.isOob = deleteFieldFn();
    }
    if (
      !('oobDate' in cleanedOverride)
      && isExplicitlyCleared(rawValues, 'oobDate')
      && hadOverrideField(existingOverride, 'oobDate')
    ) {
      payload.oobDate = deleteFieldFn();
    }
  }

  return payload;
}

export default applyClearedOverrideDeletes;
